import React, { useMemo, useState } from 'react';
import SubsurfaceViewer3D from './SubsurfaceViewer3D';
import TerrainMesh from './terrain/TerrainMesh';
import DatasetPointClouds from './DatasetPointClouds';
import RawScatter3D from './RawScatter3D';
import FenceTraces from './FenceTraces';
import Boreholes3D from './boreholes/Boreholes3D';
import useTerrainSettings from '../hooks/useTerrainSettings';
import { BASEMAP_TILE_SOURCES } from '../lib/map-styles';
import { useDemoMode } from '../hooks/useDemoMode';
import { hasXyz } from '../lib/dataset-points3d';
import { isScatterDataset } from '../lib/raw-scatter3d';
import { extractExtent } from '../lib/camera-framing';

/**
 * The 3D scene shared by the /3d page and the docked map panel: SubsurfaceViewer3D with the DEM
 * terrain, boreholes, dataset point clouds and raw-survey resistivity scatters as scene extras. `view` is the state object of
 * hooks/useViewer3dView; models come from hooks/useViewer3dModels. The caller owns the layout.
 */
const Viewer3DScene = React.forwardRef(function Viewer3DScene({
  view, datasets = [], drawnIds = [], points = null, scatterIds = [], scatter = null, onScatterHover = null, focusLine = null, fenceModels, fitModelIds = null, showPointCloud = true, style,
}, ref) {
  const terrain = useTerrainSettings();
  const [demoMode] = useDemoMode();
  const drawnDatasets = useMemo(() => datasets.filter((d) => drawnIds.includes(d.id) && hasXyz(d)), [datasets, drawnIds]);
  const clouds = points?.clouds;
  const scatterDatasets = useMemo(() => datasets.filter((d) => scatterIds.includes(d.id) && isScatterDataset(d)), [datasets, scatterIds]);
  const scatters = scatter?.scatters;
  // lowest ground of the models with topography: the flat scene's vertical origin (lib/voxel-select flatPositions), shared by the scatter
  const flatBase = useMemo(() => {
    let lo = Infinity;
    fenceModels.forEach((m) => (m.cells || []).forEach((c) => { if (typeof c.surface === 'number' && c.surface < lo) lo = c.surface; }));
    return Number.isFinite(lo) ? lo : null;
  }, [fenceModels]);
  // lon/lat extents of the drawn dataset clouds and scatters: the camera presets frame them with the models
  const contentExtents = useMemo(() => {
    const out = [];
    drawnDatasets.forEach((d) => { const e = extractExtent(clouds?.[d.id]); if (e) out.push(e); });
    scatterDatasets.forEach((d) => { const e = extractExtent(scatters?.[d.id]); if (e) out.push(e); });
    return out.length ? out : null;
  }, [drawnDatasets, clouds, scatterDatasets, scatters]);
  const { terrainReady, setTerrainReady, showFences, fenceOpacity } = view;
  const { showTraces, showBoreholes } = view;
  const [terrainInfo, setTerrainInfo] = useState(null);
  const traceFences = useMemo(() => fenceModels.filter((m) => m.line?.length === 2 && m.cells?.length && !['grid', 'mesh3d'].includes(m.model?.geometryKind)), [fenceModels]);

  return (
    <SubsurfaceViewer3D
      ref={ref}
      datasets={datasets}
      focusLine={focusLine}
      fenceModels={fenceModels}
      sceneExtras={({ origin }) => (
        <>
          <TerrainMesh origin={origin} models={fenceModels} opacity={fenceOpacity} showModels={showFences} showSections={view.showSections} showVolumes={view.showVolumes} onReady={setTerrainReady} onInfo={setTerrainInfo}
            fallback={(
              <>
                <DatasetPointClouds datasets={drawnDatasets} clouds={clouds} origin={origin} />
                <RawScatter3D datasets={scatterDatasets} scatters={scatters} origin={origin} flatBase={flatBase} onHover={onScatterHover} />
                {showBoreholes && <Boreholes3D origin={origin} />}
              </>
            )}
            renderDraped={({ groundY, refElev }) => (
              <>
                {showFences && showTraces && view.showSections && <FenceTraces fences={traceFences} origin={origin} groundY={groundY} />}
                <DatasetPointClouds datasets={drawnDatasets} clouds={clouds} origin={origin} refElev={refElev} groundY={groundY} />
                <RawScatter3D datasets={scatterDatasets} scatters={scatters} origin={origin} refElev={refElev} groundY={groundY} onHover={onScatterHover} />
                {showBoreholes && <Boreholes3D origin={origin} groundY={groundY} />}
              </>
            )} />
        </>
      )}
      terrainDraped={terrainReady && showFences}
      terrainInfo={terrainInfo}
      textureAttribution={terrain.drape3d ? BASEMAP_TILE_SOURCES[terrain.drapeStyle]?.attribution : null}
      center={{ lat: 37.7749, lng: -122.4194 }}
      style={style || { width: '100%', height: '100%' }}
      showPointCloud={showPointCloud}
      demoMode={demoMode}
      colorMode={view.colorMode}
      cameraPresetRequest={view.cameraPresetRequest}
      fitModelIds={fitModelIds}
      contentExtents={contentExtents}
      threshold={view.threshold} setThreshold={view.setThreshold}
      sliceAxis={view.sliceAxis} setSliceAxis={view.setSliceAxis}
      slicePos={view.slicePos} setSlicePos={view.setSlicePos}
      sliceThickness={view.sliceThickness} setSliceThickness={view.setSliceThickness}
      cropX={view.cropX} setCropX={view.setCropX}
      cropZ={view.cropZ} setCropZ={view.setCropZ}
      renderMode={view.renderMode} setRenderMode={view.setRenderMode}
      outsideOpacity={view.outsideOpacity} setOutsideOpacity={view.setOutsideOpacity}
      showVolumePanel={view.showVolumePanel} setShowVolumePanel={view.setShowVolumePanel}
      showFences={view.showFences} setShowFences={view.setShowFences}
      showGrid={view.showGrid} showAxes={view.showAxes} showTraces={view.showTraces} showSections={view.showSections}
      showVolumes={view.showVolumes} showOverlay={view.showOverlay}
      fenceOpacity={view.fenceOpacity} setFenceOpacity={view.setFenceOpacity}
      onResetVolumeFilters={view.resetVolumeFilters}
    />
  );
});

export default Viewer3DScene;
